import React, { useState, useEffect } from 'react';
import { X, Wrench, MapPin, FileText, AlertCircle, Building2, Camera, Paperclip, Calendar, DollarSign, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../ui/utils';
import { serviceTicketService } from '../../utils/services/serviceTicketService';
import { Vendor } from '../../types/vendor';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../ui/dialog';

interface CreateTicketFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    wells: any[];
    vendors: Vendor[];
    initialWellId?: string | null;
    currentUser: any;
    isDarkMode: boolean;
}

export function CreateTicketForm({
    isOpen,
    onClose,
    onSuccess,
    wells,
    vendors,
    initialWellId,
    currentUser,
    isDarkMode
}: CreateTicketFormProps) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        wellId: initialWellId || '',
        title: '',
        description: '',
        priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
        category: 'Maintenance',
        assignedToId: '',
        vendorId: '',
        costEstimate: '',
        scheduledDate: '',
    });

    useEffect(() => {
        if (isOpen && initialWellId) {
            setFormData(prev => ({ ...prev, wellId: initialWellId }));
        }
    }, [isOpen, initialWellId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title || !formData.wellId) {
            toast.error('Please fill in required fields');
            return;
        }

        try {
            setLoading(true);

            const selectedWell = wells.find(w => w.id === formData.wellId);
            const selectedVendor = vendors.find(v => v.id === formData.vendorId);

            const mapCategoryToType = (cat: string): any => {
                const lower = cat.toLowerCase();
                if (lower === 'maintenance') return 'preventive_maintenance';
                if (lower === 'repair') return 'repair';
                if (lower === 'inspection') return 'inspection';
                if (lower === 'emergency') return 'emergency';
                return 'other';
            };

            await serviceTicketService.createServiceTicket({
                wellId: formData.wellId,
                wellName: selectedWell?.name || 'Unknown Well',
                title: formData.title,
                description: formData.description,
                priority: formData.priority,
                type: mapCategoryToType(formData.category),
                assignedTo: formData.assignedToId || currentUser?.id,
                assignedToName: 'Assigned User',
                vendorId: formData.vendorId || undefined,
                vendorName: selectedVendor?.name || undefined,
                estimatedCost: formData.costEstimate ? parseFloat(formData.costEstimate) : undefined,
                scheduledDate: formData.scheduledDate ? new Date(formData.scheduledDate) : undefined,
            }, currentUser?.id || 'unknown', currentUser?.name || 'User');

            toast.success('Ticket created successfully');
            onSuccess();
            onClose();

            setFormData({
                wellId: initialWellId || '',
                title: '',
                description: '',
                priority: 'medium',
                category: 'Maintenance',
                assignedToId: '',
                vendorId: '',
                costEstimate: '',
                scheduledDate: '',
            });

        } catch (error) {
            console.error('Failed to create ticket:', error);
            toast.error('Failed to create ticket');
        } finally {
            setLoading(false);
        }
    };

    const categories = [
        'Maintenance', 'Repair', 'Inspection', 'Emergency', 'Electrical',
        'Plumbing', 'Software', 'Other'
    ];

    const priorityOptions = [
        { value: 'low', label: 'Low', color: 'bg-green-500' },
        { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
        { value: 'high', label: 'High', color: 'bg-orange-500' },
        { value: 'critical', label: 'Critical', color: 'bg-red-500' },
    ];

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={cn(
                "sm:max-w-[640px] max-h-[90vh] overflow-y-auto p-0 gap-0 border rounded-xl",
                isDarkMode 
                    ? "bg-slate-900 border-slate-700" 
                    : "bg-white border-gray-200"
            )}>
                {/* Header */}
                <div className={cn(
                    "px-6 py-5 border-b flex items-center justify-between",
                    isDarkMode 
                        ? "bg-slate-800 border-slate-700" 
                        : "bg-gray-50 border-gray-200"
                )}>
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "p-2.5 rounded-lg",
                            isDarkMode ? "bg-rose-500/20" : "bg-rose-100"
                        )}>
                            <Wrench className={cn(
                                "w-5 h-5",
                                isDarkMode ? "text-rose-400" : "text-rose-600"
                            )} />
                        </div>
                        <div>
                            <h2 className={cn(
                                "text-lg font-bold",
                                isDarkMode ? "text-white" : "text-gray-900"
                            )}>
                                Create Service Ticket
                            </h2>
                            <p className={cn(
                                "text-sm",
                                isDarkMode ? "text-slate-400" : "text-gray-500"
                            )}>
                                Log a new maintenance request
                            </p>
                        </div>
                    </div>
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={onClose}
                        className={cn(
                            "h-9 w-9 rounded-lg",
                            isDarkMode 
                                ? "hover:bg-slate-700 text-slate-400" 
                                : "hover:bg-gray-100 text-gray-500"
                        )}
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Well Selection */}
                    <div className="space-y-2">
                        <Label className={cn(
                            "flex items-center gap-2 font-semibold",
                            isDarkMode ? "text-white" : "text-gray-900"
                        )}>
                            <MapPin className="w-4 h-4 text-rose-500" />
                            Well Location <span className="text-red-500">*</span>
                        </Label>
                        <select
                            className={cn(
                                "w-full rounded-lg border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500",
                                isDarkMode 
                                    ? "bg-slate-800 border-slate-600 text-white" 
                                    : "bg-white border-gray-300 text-gray-900"
                            )}
                            value={formData.wellId}
                            onChange={(e) => setFormData({ ...formData, wellId: e.target.value })}
                            required
                        >
                            <option value="">Select a Well</option>
                            {wells.map(well => (
                                <option key={well.id} value={well.id}>
                                    {well.name} {well.wellNumber ? `(${well.wellNumber})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Title */}
                    <div className="space-y-2">
                        <Label className={cn(
                            "flex items-center gap-2 font-semibold",
                            isDarkMode ? "text-white" : "text-gray-900"
                        )}>
                            <FileText className="w-4 h-4 text-rose-500" />
                            Title <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            placeholder="e.g. Pump Failure on Well #4"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className={cn(
                                "rounded-lg h-11",
                                isDarkMode 
                                    ? "bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                                    : "bg-white border-gray-300"
                            )}
                            required
                        />
                    </div>

                    {/* Priority & Category */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className={cn(
                                "font-semibold",
                                isDarkMode ? "text-white" : "text-gray-900"
                            )}>
                                Priority
                            </Label>
                            <div className="grid grid-cols-4 gap-1">
                                {priorityOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, priority: opt.value as any })}
                                        className={cn(
                                            "py-2 px-2 text-xs font-medium rounded-lg border transition-all",
                                            formData.priority === opt.value
                                                ? `${opt.color} text-white border-transparent`
                                                : isDarkMode
                                                    ? "bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500"
                                                    : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                                        )}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className={cn(
                                "font-semibold",
                                isDarkMode ? "text-white" : "text-gray-900"
                            )}>
                                Category
                            </Label>
                            <select
                                className={cn(
                                    "w-full rounded-lg border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500",
                                    isDarkMode 
                                        ? "bg-slate-800 border-slate-600 text-white" 
                                        : "bg-white border-gray-300 text-gray-900"
                                )}
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Vendor */}
                    <div className="space-y-2">
                        <Label className={cn(
                            "flex items-center gap-2 font-semibold",
                            isDarkMode ? "text-white" : "text-gray-900"
                        )}>
                            <Building2 className="w-4 h-4 text-rose-500" />
                            Assign Vendor <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                        </Label>
                        <select
                            className={cn(
                                "w-full rounded-lg border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500",
                                isDarkMode 
                                    ? "bg-slate-800 border-slate-600 text-white" 
                                    : "bg-white border-gray-300 text-gray-900"
                            )}
                            value={formData.vendorId}
                            onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                        >
                            <option value="">No Vendor Assigned</option>
                            {vendors.filter(v => v.status === 'active').map(vendor => (
                                <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label className={cn(
                            "flex items-center gap-2 font-semibold",
                            isDarkMode ? "text-white" : "text-gray-900"
                        )}>
                            <AlertCircle className="w-4 h-4 text-rose-500" />
                            Description
                        </Label>
                        <Textarea
                            placeholder="Describe the issue in detail..."
                            className={cn(
                                "min-h-[100px] rounded-lg resize-none",
                                isDarkMode 
                                    ? "bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                                    : "bg-white border-gray-300"
                            )}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>

                    {/* Schedule & Cost */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className={cn(
                                "flex items-center gap-2 font-semibold",
                                isDarkMode ? "text-white" : "text-gray-900"
                            )}>
                                <Calendar className="w-4 h-4 text-rose-500" />
                                Scheduled Date
                            </Label>
                            <Input
                                type="date"
                                value={formData.scheduledDate}
                                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                                className={cn(
                                    "rounded-lg h-11",
                                    isDarkMode 
                                        ? "bg-slate-800 border-slate-600 text-white" 
                                        : "bg-white border-gray-300"
                                )}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className={cn(
                                "flex items-center gap-2 font-semibold",
                                isDarkMode ? "text-white" : "text-gray-900"
                            )}>
                                <DollarSign className="w-4 h-4 text-rose-500" />
                                Cost Estimate
                            </Label>
                            <Input
                                type="number"
                                placeholder="0.00"
                                value={formData.costEstimate}
                                onChange={(e) => setFormData({ ...formData, costEstimate: e.target.value })}
                                className={cn(
                                    "rounded-lg h-11",
                                    isDarkMode 
                                        ? "bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                                        : "bg-white border-gray-300"
                                )}
                            />
                        </div>
                    </div>

                    {/* Attachments */}
                    <div className="space-y-2">
                        <Label className={cn(
                            "flex items-center gap-2 font-semibold",
                            isDarkMode ? "text-white" : "text-gray-900"
                        )}>
                            <Camera className="w-4 h-4 text-rose-500" />
                            Attachments
                        </Label>
                        <div className={cn(
                            "border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center gap-2",
                            isDarkMode 
                                ? "border-slate-600 hover:border-slate-500" 
                                : "border-gray-300 hover:border-gray-400"
                        )}>
                            <div className="flex gap-2">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    className={cn(
                                        "gap-2 rounded-lg",
                                        isDarkMode 
                                            ? "border-slate-600 hover:bg-slate-700" 
                                            : "border-gray-300 hover:bg-gray-50"
                                    )}
                                >
                                    <Camera className="w-4 h-4" />
                                    Photo
                                </Button>
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    className={cn(
                                        "gap-2 rounded-lg",
                                        isDarkMode 
                                            ? "border-slate-600 hover:bg-slate-700" 
                                            : "border-gray-300 hover:bg-gray-50"
                                    )}
                                >
                                    <Paperclip className="w-4 h-4" />
                                    File
                                </Button>
                            </div>
                            <p className={cn(
                                "text-xs",
                                isDarkMode ? "text-slate-500" : "text-gray-400"
                            )}>
                                Drag files here or click to upload
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={cn(
                        "flex justify-end gap-3 pt-4 border-t",
                        isDarkMode ? "border-slate-700" : "border-gray-200"
                    )}>
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={onClose}
                            className={cn(
                                "rounded-lg px-5",
                                isDarkMode 
                                    ? "border-slate-600 hover:bg-slate-700 text-slate-300" 
                                    : "border-gray-300 hover:bg-gray-50"
                            )}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            disabled={loading}
                            className="rounded-lg px-5 bg-rose-600 hover:bg-rose-700 text-white"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating...
                                </span>
                            ) : (
                                'Create Ticket'
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
